/**
 * PropertyTable Component
 * 
 * Displays a table of global properties with search and refresh functionality.
 * Extracted from GlobalPropertyTableView for better separation of concerns.
 */

import React from 'react';
import { Table, Card, Input, Space, Button, Typography, Tag } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { PropertyTableRow } from '../../hooks/useGlobalPropertyTable';
import type { ColumnsType } from 'antd/es/table';

const { Search } = Input;
const { Title } = Typography;

interface PropertyTableProps {
  columns: ColumnsType<PropertyTableRow>;
  dataSource: PropertyTableRow[];
  loading: boolean;
  searchText: string;
  onSearchChange: (_value: string) => void;
  onRefresh: () => void;
  onRowClick?: (_record: PropertyTableRow) => void;
  selectedPropertyKey?: string | null;
  showSplitPane?: boolean;
  title?: React.ReactNode;
  className?: string;
}

export const PropertyTable: React.FC<PropertyTableProps> = ({
  columns,
  dataSource,
  loading,
  searchText,
  onSearchChange,
  onRefresh,
  onRowClick,
  selectedPropertyKey,
  showSplitPane = false,
  title,
  className = 'properties-list-card'
}) => {
  const defaultTitle = (
    <Space>
      <Title level={4} style={{ margin: 0 }}>Global Properties</Title>
      <Tag>{dataSource.length} properties</Tag>
    </Space>
  );

  const tableProps: any = {
    columns,
    dataSource,
    loading,
    size: 'small' as const,
    rowKey: 'key',
    pagination: showSplitPane
      ? {
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total: number) => `Total ${total} properties`,
          position: ['bottomCenter'] as const
        }
      : {
          pageSize: 50,
          showSizeChanger: true,
          showTotal: (total: number) => `Total ${total} properties`
        }
  };

  if (showSplitPane) {
    tableProps.scroll = { y: 'calc(100vh - 400px)' };
    tableProps.onRow = onRowClick
      ? (record: PropertyTableRow) => ({
          onClick: () => onRowClick(record),
          style: {
            cursor: 'pointer',
            backgroundColor: selectedPropertyKey === record.property_key ? '#e6f7ff' : 'transparent'
          }
        })
      : undefined;
    tableProps.rowClassName = selectedPropertyKey
      ? (record: PropertyTableRow) =>
          selectedPropertyKey === record.property_key ? 'selected-property-row' : ''
      : undefined;
  }

  return (
    <Card
      title={title || defaultTitle}
      extra={
        <Space>
          {showSplitPane ? (
            <Search
              placeholder="Search properties..."
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
              style={{ width: 200 }}
              allowClear
            />
          ) : (
            <Input
              placeholder="Search properties..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
              style={{ width: 250 }}
              allowClear
            />
          )}
          <Button icon={<ReloadOutlined />} onClick={onRefresh} size={showSplitPane ? 'small' : 'middle'}>
            Refresh
          </Button>
        </Space>
      }
      className={className}
    >
      <Table {...tableProps} />
    </Card>
  );
};
